'use client';

import { c as _c } from "react/compiler-runtime";
import './geo-banner.scss';
import { deleteCookie, getCookie } from 'cookies-next/client';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Intl } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Prominence, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { setCICookie } from '@sola/tracking/lib/cookies.ts';
import { FeatureBase } from '../../molecules/feature-base/feature-base.tsx';
import { FlexibleSection } from '../flexible-section/flexible-section.tsx';
export type GeolocationBannerDetailsProps = {
  countryName: string;
  country: string;
  url: string;
};
enum CountryCodes {
  ca = 'ca',
}
type GeolocationRedirectDetails = { [key in CountryCodes]: GeolocationBannerDetailsProps };
const CountryDetails: GeolocationRedirectDetails = {
  [CountryCodes.ca]: {
    country: 'Canadian',
    countryName: 'Canada',
    url: 'https://www.compassion.ca/'
  }
};
const radioValues = {
  yes: 'Yes',
  no: 'No'
};
const geoCookieName = 'ignoreGeoRedirect';
const dontAskAgainCookie = getCookie(geoCookieName);
const InnerGeoBanner = ({
  countryCode,
  onClose
}: {
  countryCode?: CountryCodes;
  onClose: () => void;
}) => {
  const router = useRouter();
  const {
    t
  } = useTranslation(['core', 'geo']);
  const handleDontAskToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setCICookie(new URLSearchParams(`${geoCookieName}=true`), [geoCookieName]);
    } else {
      deleteCookie(geoCookieName);
    }
  };
  if (!countryCode || !CountryDetails[countryCode as CountryCodes]) {
    return null;
  }
  return <FeatureBase className="GeoBanner" body={<div className="GeoBanner__content">
          <div className="GeoBanner__radioGroup">
            <Text variant={Variants.Label} prominence={Prominence.Potent} size={Sizes.XLarge}>
              <Intl k="geo:redirectBanner.title" variables={{
          countryName: CountryDetails[countryCode as CountryCodes]?.countryName,
          country: CountryDetails[countryCode as CountryCodes]?.country
        }} />
            </Text>
            <RadioGroup name="geo-redirect" variant="buttons" onChange={(v: React.ChangeEvent<HTMLInputElement>) => {
        if (v.target.value === radioValues.no) {
          onClose();
        }
        if (v.target.value === radioValues.yes) {
          try {
            router.push(CountryDetails[countryCode as CountryCodes]?.url);
          } catch (error) {
            console.error('Geo redirect error:', error);
          }
        }
      }} items={[{
        value: radioValues.yes,
        text: t('core:actions.yes')
      }, {
        value: radioValues.no,
        text: t('core:actions.no')
      }]} />
          </div>
          <div className="GeoBanner__footer">
            <Checkbox label={t('geo:redirectBanner.dontAskAgain')} name="geoRedirectDontAskAgain" onChange={(e_0: React.ChangeEvent<HTMLInputElement>) => handleDontAskToggle(e_0)} />
            <Text size={Sizes.Medium} prominence={Prominence.Potent} variant={Variants.Label}>
              <Intl k="geo:redirectBanner.note" variables={{
          country: CountryDetails[countryCode as CountryCodes]?.country
        }} />
            </Text>
          </div>
        </div>} />;
};
export const GeoBanner = () => {
  const $ = _c(6);
  const [open, setOpen] = useState(false);
  const [geo, setGeo] = useState();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const countryCode = new URLSearchParams(window.location.search).get("geo");
      if (countryCode && !dontAskAgainCookie) {
        setGeo(countryCode);
        setOpen(true);
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  if (!open) {
    return null;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      layoutRatio: "1-1",
      theme: "darkBlue",
      fullWidth: true
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = geo as CountryCodes;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setOpen(false);
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3) {
    t5 = <FlexibleSection meta={t2} children1={<InnerGeoBanner countryCode={t3} onClose={t4} />} />;
    $[4] = t3;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};